import React, { FC, useMemo } from 'react';
import { Nav } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import BackButton from '../../components/BackButton/BackButton';
import GenericFormattedDate from '../../components/GenericFormattedDate/GenericFormattedDate';

import Header from '../../components/Header/Header';
import ReportButton from '../../components/Report/ReportButton';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import dataPlaceholders from '../../constants/dataPlaceholders';
import useNotifications from '../../hooks/useNotifications';
import EntityTypes from '../../models/Report/EntityTypes';
import ReportData from '../../models/Report/ReportData';
import productService from '../../services/productService';
import ProductDashAppData from '../../models/DashApp/ProductDashAppData';
import DashAppButton from '../../components/DashAppButton/DashAppButton';

const ProductOverview: FC = () => {
  const { id: productId } = useParams<'id'>();
  const intl = useIntl();
  const { notify } = useNotifications();
  const { data: status } = useQuery(['productStatus', productId], () => productService.getStatus(productId!), {
    onError: () =>
      notify(
        'error',
        intl.formatMessage({ id: 'productsOverview.notification.title' }),
        intl.formatMessage({ id: 'productsOverview.notification.unableLoadDetails' }),
      ),
  });

  const productDashAppData: ProductDashAppData | undefined = useMemo(() => {
    if (!status) {
      return undefined;
    }

    return {
      productId: status.id,
      companyId: status.companyId,
    };
  }, [status, productId]);

  const reportData: ReportData = useMemo(() => {
    if (status) {
      return {
        productName: status.name,
        entityType: EntityTypes.product,
        entityId: status.id,
      };
    }
    return {
      productName: '',
      entityType: EntityTypes.product,
      entityId: '',
    };
  }, [status]);

  return (
    <>
      <Header title={status?.name || ''}>
        <div className="d-flex gap-4 align-items-baseline">
          <StatusBadge isActive={status?.isActive || false} className="size-caption" />
          <span className="size-caption">
            <FormattedMessage id="productsOverview.header.phase" />{' '}
            <span data-test-id="productsOverview-header-phase">{`${
              status?.phaseName || dataPlaceholders.missingData
            }`}</span>
          </span>
          <span className="size-caption">
            <FormattedMessage id="productsOverview.header.updatedAt" />{' '}
            <span data-test-id="productsOverview-header-updatedAt">
              <GenericFormattedDate value={status?.updatedAt} />
            </span>
          </span>
          {status?.isDashAppEnabled && productDashAppData && <DashAppButton productDashAppData={productDashAppData} />}
          <ReportButton reportData={reportData} fullSize />
          <BackButton />
        </div>
      </Header>
      <div className="page-content px-5 mb-4" id="productOverviewContainer">
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link as={NavLink} to="details" data-test-id="tab-details">
              <FormattedMessage id="productsOverview.title.detail" />
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="status" data-test-id="tab-status">
              <FormattedMessage id="productsOverview.title.status" />
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="steps" data-test-id="tab-steps">
              <FormattedMessage id="productsOverview.title.steps" values={{ value: status?.totalSteps }} />
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="campaigns" data-test-id="tab-campaings">
              <FormattedMessage id="productsOverview.title.campaigns" values={{ value: status?.totalCampaings }} />
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="alerts" data-test-id="tab-alerts">
              <FormattedMessage id="productsOverview.title.alerts" values={{ value: status?.totalAlerts }} />
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="updates" data-test-id="tab-updates">
              <FormattedMessage id="productsOverview.title.updates" values={{ value: status?.totalUpdates }} />
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <Outlet />
      </div>
    </>
  );
};

export default ProductOverview;
